import { ExternalLink, FileSpreadsheet, Folder, FolderOpen, Wrench, Edit, UserCheck, Settings, Import, ChevronDown, FolderPlus, RefreshCw, Trash2, Copy, Eraser } from 'lucide-react';
import { useCheckPermission } from '@/hooks/useUserRoles';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Project } from '@/types/project';
import { ProjectPermission } from '@/types/auth';
import { useDialogState } from '@/hooks/useDialogState';
import { useCanManageProjectPermissions } from '@/hooks/useCanManageProjectPermissions';
import { useProjectTableIconPermissions } from '@/hooks/useProjectTableIconPermissions';
import { useCanCreateNewGDriveFolders } from '@/hooks/useCanCreateNewGDriveFolders';
import ProjectEditDialog from './ProjectEditDialog';
import ProjectPermissionsDialog from '@/components/admin/ProjectPermissionsDialog';
import { ProjectImportDialog } from './ProjectImportDialog';
import { StakeholderListDialog } from './StakeholderListDialog';
import { StakeholderErrorBoundary } from '@/components/admin/StakeholderErrorBoundary';
import { CreateProjectGDriveFoldersDialog } from './CreateProjectGDriveFoldersDialog';
import { SyncGDrivePermissionsDialog } from './SyncGDrivePermissionsDialog';
import { ClearNewGDriveFoldersDialog } from './ClearNewGDriveFoldersDialog';
import { CreateCostSheetDialog } from './CreateCostSheetDialog';
import { ProjectCopyDataDialog } from './ProjectCopyDataDialog';
import { ProjectDeleteCopiedDataDialog } from './ProjectDeleteCopiedDataDialog';
import { useMemo, memo, useState } from 'react';
import { buildGoogleDriveFolderUrl } from '@/lib/googleDrive';
// ✅ RETIRED 2026-05-01 (PR-F2): globalFeatureFlags import removed (ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS retired)
import { useConsolidatedPermissions, type ConsolidatedPermissions } from '@/hooks/useConsolidatedPermissions';

interface ProjectQuickActionsProps {
  project: Project;
  permissions?: ProjectPermission;
}

const ProjectQuickActions = memo(({ project, permissions }: ProjectQuickActionsProps) => {
  // Dialog states
  const editDialog = useDialogState();
  const permissionsDialog = useDialogState();
  const importDialog = useDialogState();
  const stakeholderDialog = useDialogState();
  const createGDriveFoldersDialog = useDialogState();
  const syncPermissionsDialog = useDialogState();
  const clearGDriveFoldersDialog = useDialogState();
  const createCostSheetDialog = useDialogState();
  const copyDataDialog = useDialogState();
  const deleteCopiedDataDialog = useDialogState();

  // Always start with all dropdowns closed when entering project details
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };
  
  // Check if user can manage project permissions
  const canManagePermissions = useCanManageProjectPermissions(project.id);
  
  // Get ICON_* permissions for visibility control
  const { permissions: iconPermissions, isLoading: isLoadingPermissions } = useProjectTableIconPermissions();
  
  // Get consolidated permissions for folder-specific filtering
  const { data: consolidatedPerms, isLoading: isLoadingConsolidatedPerms } = useConsolidatedPermissions();
  
  // Check CREATE_NEW_GDRIVE_FOLDERS permission
  const { canCreate: canCreateNewGDrive } = useCanCreateNewGDriveFolders();
  
  // Check STAKEHOLDERS_ACCESS permission
  const { data: canAccessStakeholders = false } = useCheckPermission('STAKEHOLDERS_ACCESS');
  
  // Check PROJECTS_DUPLICATE permission for copy/delete data
   const { data: canDuplicateProjects = false } = useCheckPermission('PROJECTS_DUPLICATE');
   
   // Gate the Tools trigger button: only show if at least one tool is available
   const hasAnyTool = canAccessStakeholders || iconPermissions.editProject || canManagePermissions || iconPermissions.importProject || canCreateNewGDrive || canDuplicateProjects;
   

  
   // ✅ RETIRED 2026-05-01 (PR-F2): ENABLE_NEW_GDRIVE_FOLDER_PERMISSIONS flag removed — always use consolidatedPerms
  const allGsheetFiles = [
    { key: 'proc_file', label: 'Proc', url: project.proc_file, permission: 'procurement' },
    { key: 'doc_file', label: 'Doc', url: project.doc_file, permission: 'documentation' },
    { key: 'cost_file', label: 'Cost', url: project.cost_file, permission: 'cost' },
    { key: 'shop_file', label: 'Shop', url: project.shop_file, permission: 'shopDrawing' },
  ] as const;

  const projectWithFolders = project as any;
  const allGdriveFolders = [
    { key: 'build_folder', label: 'Build', url: projectWithFolders.build_folder, permission: 'buildFolder' },
    { key: 'doc_folder', label: 'Doc', url: projectWithFolders.doc_folder, permission: 'docFolder' },
    { key: 'req_folder', label: 'Req', url: projectWithFolders.req_folder, permission: 'reqFolder' },
    { key: 'proposal_folder', label: 'Proposal', url: projectWithFolders.proposal_folder, permission: 'proposalFolder' },
    { key: 'tech_folder', label: 'Tech Report', url: projectWithFolders.tech_folder, permission: 'techFolder' },
    { key: 'report_folder', label: 'Report', url: projectWithFolders.report_folder, permission: 'reportFolder' },
    { key: 'specs_folder', label: 'Specs', url: projectWithFolders.specs_folder, permission: 'specsFolder' },
    { key: 'po_folder', label: 'PO', url: projectWithFolders.po_folder, permission: 'poFolder' },
    { key: 'shop_folder', label: 'Shop', url: projectWithFolders.shop_folder, permission: 'shopFolder' },
  ] as const;
  
  // Filter files/folders based on ICON_* permissions AND URL presence
  const visibleGsheetFiles = useMemo(() => {
    return allGsheetFiles.filter(file => 
      file.url && iconPermissions[file.permission]
    );
  }, [iconPermissions]);
  
  const visibleGdriveFolders = useMemo(() => {
    return allGdriveFolders.filter(folder => 
      folder.url && iconPermissions[folder.permission]
    );
  }, [iconPermissions]);

  // NEW Google Drive folders with specific permission mapping (Option B: separate DOC subfolder permissions)
  const allNewGdriveFolders = [
    { key: 'new_build_folder', label: 'Build', url: buildGoogleDriveFolderUrl(projectWithFolders.new_build_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_BUILD' as keyof ConsolidatedPermissions },
    { key: 'new_specs_folder', label: 'Specs', url: buildGoogleDriveFolderUrl(projectWithFolders.new_specs_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_SPECS' as keyof ConsolidatedPermissions },
    { key: 'new_shop_folder', label: 'Shop', url: buildGoogleDriveFolderUrl(projectWithFolders.new_shop_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_SHOP' as keyof ConsolidatedPermissions },
    { key: 'new_po_folder', label: 'PO', url: buildGoogleDriveFolderUrl(projectWithFolders.new_po_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_PO' as keyof ConsolidatedPermissions },
    { key: 'new_cost_folder', label: 'Cost', url: buildGoogleDriveFolderUrl(projectWithFolders.new_cost_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_COST' as keyof ConsolidatedPermissions },
    // ✅ Option B: Each DOC subfolder has its own specific permission
    { key: 'new_tech_folder', label: 'Tech', url: buildGoogleDriveFolderUrl(projectWithFolders.new_tech_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_DOC_TECH' as keyof ConsolidatedPermissions },
    { key: 'new_req_folder', label: 'Req', url: buildGoogleDriveFolderUrl(projectWithFolders.new_req_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_DOC_REQ' as keyof ConsolidatedPermissions },
    { key: 'new_proposal_folder', label: 'Proposal', url: buildGoogleDriveFolderUrl(projectWithFolders.new_proposal_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_DOC_PROPOSAL' as keyof ConsolidatedPermissions },
    { key: 'new_report_folder', label: 'Report', url: buildGoogleDriveFolderUrl(projectWithFolders.new_report_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_DOC_REPORT' as keyof ConsolidatedPermissions },
    { key: 'new_contract_folder', label: 'Contract', url: buildGoogleDriveFolderUrl(projectWithFolders.new_contract_folder), permission: 'newGdriveFolders', viewPermission: 'VIEW_FOLDER_DOC_CONTRACT' as keyof ConsolidatedPermissions },
    // Note: new_doc_folder is NOT shared directly - only subfolders are shared
  ] as const;

   const visibleNewGdriveFolders = useMemo(() => {
    const filtered = allNewGdriveFolders.filter(folder => {
      if (!folder.url) return false;
      
      // ✅ RETIRED 2026-05-01 (PR-F2): Legacy iconPermissions path removed — always use specific VIEW_FOLDER_* permission
      return consolidatedPerms?.[folder.viewPermission] === true;
    });
    
    return filtered;
  }, [consolidatedPerms]);

  // Show loading skeleton while permissions are loading
  const isLoadingAll = isLoadingPermissions || isLoadingConsolidatedPerms;
  if (isLoadingAll) {
    return <Skeleton className="h-24 w-full" />;
  }

  return (
    <>
      {/* Horizontal buttons bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Google Sheets button */}
        {visibleGsheetFiles.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleSection('gsheet')}
            className={cn(
              "flex items-center gap-1.5",
              openSection === 'gsheet' && "bg-accent"
            )}
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span className="hidden sm:inline">Google Sheets</span>
            <span className="sm:hidden">GSheets</span>
            <span className="text-xs text-muted-foreground">({visibleGsheetFiles.length})</span>
            <ChevronDown className={cn(
              "h-3 w-3 transition-transform",
              openSection === 'gsheet' && "rotate-180"
            )} />
          </Button>
        )}

        {/* Google Drive button */}
        {visibleGdriveFolders.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleSection('gdrive')}
            className={cn(
              "flex items-center gap-1.5",
              openSection === 'gdrive' && "bg-accent"
            )}
          >
            <Folder className="h-4 w-4" />
            <span className="hidden sm:inline">Google Drive</span>
            <span className="sm:hidden">GDrive</span>
            <span className="text-xs text-muted-foreground">({visibleGdriveFolders.length})</span>
            <ChevronDown className={cn(
              "h-3 w-3 transition-transform",
              openSection === 'gdrive' && "rotate-180"
            )} />
          </Button>
        )}

        {/* NEW Google Drive button */}
        {visibleNewGdriveFolders.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleSection('new-gdrive')}
            className={cn(
              "flex items-center gap-1.5",
              openSection === 'new-gdrive' && "bg-accent"
            )}
          >
            <FolderOpen className="h-4 w-4" />
            <span className="hidden sm:inline">NEW Google Drive</span>
            <span className="sm:hidden">NEW GDrive</span>
            <span className="text-xs text-muted-foreground">({visibleNewGdriveFolders.length})</span>
            <ChevronDown className={cn(
              "h-3 w-3 transition-transform",
              openSection === 'new-gdrive' && "rotate-180"
            )} />
          </Button>
        )}

        {/* Open Cost - only show if NOT already visible in Google Sheets dropdown */}
        {project.cost_file && consolidatedPerms?.VIEW_FOLDER_COST && !iconPermissions.cost && (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
            asChild
          >
            <a href={project.cost_file} target="_blank" rel="noopener noreferrer">
              <FileSpreadsheet className="h-4 w-4" />
              <span>Open Cost</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        )}

        {/* Tools button */}
        {hasAnyTool && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleSection('tools')}
            className={cn(
              "flex items-center gap-1.5",
              openSection === 'tools' && "bg-accent"
            )}
          >
            <Wrench className="h-4 w-4" />
            <span>Tools</span>
            <ChevronDown className={cn(
              "h-3 w-3 transition-transform",
              openSection === 'tools' && "rotate-180"
            )} />
          </Button>
        )}
      </div>


      {/* Content container - BELOW buttons, full width */}
      <div className="w-full">
        {openSection === 'gsheet' && visibleGsheetFiles.length > 0 && (
          <div className="mt-2 border rounded-lg p-3 bg-card animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {visibleGsheetFiles.map((file) => (
                <Button
                  key={file.key}
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-2 h-auto py-1.5"
                  asChild
                >
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate text-xs">{file.label}</span>
                  </a>
                </Button>
              ))}
            </div>
          </div>
        )}

        {openSection === 'gdrive' && visibleGdriveFolders.length > 0 && (
          <div className="mt-2 border rounded-lg p-3 bg-card animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
              {visibleGdriveFolders.map((folder) => (
                <Button
                  key={folder.key}
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-2 h-auto py-1.5"
                  asChild
                >
                  <a href={folder.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate text-xs">{folder.label}</span>
                  </a>
                </Button>
              ))}
            </div>
          </div>
        )}

        {openSection === 'new-gdrive' && visibleNewGdriveFolders.length > 0 && (
          <div className="mt-2 border rounded-lg p-3 bg-card animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
              {visibleNewGdriveFolders.map((folder) => (
                <Button
                  key={folder.key}
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-2 h-auto py-1.5"
                  asChild
                >
                  <a href={folder.url!} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate text-xs">{folder.label}</span>
                  </a>
                </Button>
              ))}
            </div>
          </div>
        )}

        {openSection === 'tools' && (
          <div className="mt-2 border rounded-lg p-3 bg-card animate-in fade-in slide-in-from-top-2">
              <div className="flex flex-wrap gap-1.5">
              {canAccessStakeholders && (
                <StakeholderErrorBoundary>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={stakeholderDialog.open}
                    className="justify-start gap-2 h-auto py-1.5"
                  >
                    <UserCheck className="h-3.5 w-3.5" />
                    <span className="text-xs">Stakeholder</span>
                  </Button>
                </StakeholderErrorBoundary>
              )}

              {/* Edit button - uses ICON_EDIT_PROJECT permission (synced with ProjectTable) */}
              {iconPermissions.editProject && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={editDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <Edit className="h-3.5 w-3.5" />
                  <span className="text-xs">Edit</span>
                </Button>
              )}

              {/* Permissions button - uses canManageProjectPermissions (Admin/Owner/TeamLead/PM/SOL) */}
              {canManagePermissions && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={permissionsDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <Settings className="h-3.5 w-3.5" />
                  <span className="text-xs">Permissions</span>
                </Button>
              )}

              {iconPermissions.importProject && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={importDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <Import className="h-3.5 w-3.5" />
                  <span className="text-xs">Import</span>
                </Button>
              )}

              {canCreateNewGDrive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={createGDriveFoldersDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                  <span className="text-xs">NEW GDrive</span>
                </Button>
              )}
              
              {canCreateNewGDrive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={syncPermissionsDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span className="text-xs">Sync Permissions</span>
                </Button>
              )}
              
              {canCreateNewGDrive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={createCostSheetDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  <span className="text-xs">Cost Sheet</span>
                </Button>
              )}
              
              {canCreateNewGDrive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearGDriveFoldersDialog.open}
                  className="justify-start gap-2 h-auto py-1.5 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="text-xs">Clear GDrive</span>
                </Button>
              )}
              
              {/* Copy data from another project */}
              {canDuplicateProjects && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyDataDialog.open}
                  className="justify-start gap-2 h-auto py-1.5"
                >
                  <Copy className="h-3.5 w-3.5" />
                  <span className="text-xs">Copy Data</span>
                </Button>
              )}
              
              {/* Delete copied data */}
              {canDuplicateProjects && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deleteCopiedDataDialog.open}
                  className="justify-start gap-2 h-auto py-1.5 text-destructive hover:text-destructive"
                >
                  <Eraser className="h-3.5 w-3.5" />
                  <span className="text-xs">Xóa Copy</span>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Dialogs */}
      <ProjectEditDialog
        projectId={project.id}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
      />
      
      <ProjectPermissionsDialog
        projectId={project.id}
        projectName={project.project_name}
        isOpen={permissionsDialog.isOpen}
        onClose={permissionsDialog.close}
        defaultCompany={project.company as 'ENP' | 'Solid' | 'all' || 'all'}
      />
      
      <ProjectImportDialog
        project={project}
        isOpen={importDialog.isOpen}
        onClose={importDialog.close}
      />
      
      <StakeholderErrorBoundary>
        <StakeholderListDialog
          project={project}
          isOpen={stakeholderDialog.isOpen}
          onClose={stakeholderDialog.close}
        />
      </StakeholderErrorBoundary>
      
      {canCreateNewGDrive && (
        <CreateProjectGDriveFoldersDialog
          project={project}
          isOpen={createGDriveFoldersDialog.isOpen}
          onClose={createGDriveFoldersDialog.close}
        />
      )}
      
      {canCreateNewGDrive && (
        <SyncGDrivePermissionsDialog
          projectId={project.id}
          projectName={project.project_name}
          isOpen={syncPermissionsDialog.isOpen}
          onClose={syncPermissionsDialog.close}
        />
      )}
      
      {canCreateNewGDrive && (
        <ClearNewGDriveFoldersDialog
          project={project}
          isOpen={clearGDriveFoldersDialog.isOpen}
          onClose={clearGDriveFoldersDialog.close}
        />
      )}
      
      {canCreateNewGDrive && (
        <CreateCostSheetDialog
          project={project}
          isOpen={createCostSheetDialog.isOpen}
          onClose={createCostSheetDialog.close}
        />
      )}
      
      {/* Copy Data Dialog */}
      {canDuplicateProjects && (
        <ProjectCopyDataDialog
          projectId={project.id}
          projectName={project.project_name}
          isOpen={copyDataDialog.isOpen}
          onClose={copyDataDialog.close}
        />
      )}
      
      {/* Delete Copied Data Dialog */}
      {canDuplicateProjects && (
        <ProjectDeleteCopiedDataDialog
          projectId={project.id}
          projectName={project.project_name}
          isOpen={deleteCopiedDataDialog.isOpen}
          onClose={deleteCopiedDataDialog.close}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {
  return prevProps.project.id === nextProps.project.id &&
         prevProps.permissions?.can_edit === nextProps.permissions?.can_edit;
});

ProjectQuickActions.displayName = 'ProjectQuickActions';

export default ProjectQuickActions;